import { useRef, useState } from 'react';
import { timeAgo } from '../../lib/stories';
import { useAudioPlayer } from '../../context/AudioPlayerProvider';
import { useVideoPlayer } from '../../context/VideoPlayerProvider';
import './StoryCard.css';

// The feed card formats. Each takes a normalised story (see mapStory in
// lib/stories.js). StoryCardStandard is the everyday format; the rest are
// "special" formats reserved for a situation (a lead item, a columnist, a
// podcast) and are not meant to repeat down a list.

// Standard: the workhorse. Headline and thumbnail share the top row, with
// the meta running the full width beneath both. Sized to stack into a list
// without any one item shouting, so the meta stays to the section and the
// time — no byline, no read time.
export function StoryCardStandard({ story }) {
  const { play: playVideo } = useVideoPlayer();

  return (
    <article className="story-card story-card--standard">
      <h2 className="story-card__headline">{story.headline}</h2>

      {story.image && (
        <div className="story-card__thumb-wrap">
          <img
            className="story-card__image story-card__thumb"
            src={story.image}
            alt=""
            loading="lazy"
          />
          {story.videoUrl && (
            <button
              type="button"
              className="story-card__video-badge"
              onClick={() => playVideo(story)}
              aria-label={`Watch video for ${story.headline}`}
            >
              <PlayIcon />
            </button>
          )}
        </div>
      )}

      <p className="story-card__meta">
        <span className="story-card__meta-text">
          {story.section && <span>{story.section}</span>}
          {story.publishedAt && <span>{timeAgo(story.publishedAt)}</span>}
          {story.articleAudioUrl && <AudioBadge story={story} />}
        </span>
        <KebabButton />
      </p>
    </article>
  );
}

// Cartoon: the hero treatment stripped to the image, because the drawing is
// the whole story. The headline carries as alt text rather than on the card,
// as there is no visible text for it to duplicate.
export function StoryCardCartoon({ story }) {
  return (
    <article className="story-card story-card--hero story-card--cartoon">
      <img
        className="story-card__image"
        src={story.image}
        alt={story.headline}
        loading="lazy"
      />
    </article>
  );
}

// Image on top, headline and meta below.
export function StoryCardHero({ story }) {
  return (
    <article className="story-card story-card--hero">
      <CardMedia story={story} />
      <div className="story-card__heading">
        {story.section && (
          <span className="story-card__eyebrow">{story.section}</span>
        )}
        <h2 className="story-card__headline">{story.headline}</h2>
      </div>
      <StoryMeta story={story} />
      <StoryFooter story={story} />
    </article>
  );
}

// Large headline first, image below the meta row.
export function StoryCardHeadline({ story }) {
  return (
    <article className="story-card story-card--headline">
      <div className="story-card__heading">
        {story.section && (
          <span className="story-card__eyebrow">{story.section}</span>
        )}
        <h2 className="story-card__headline">
          {story.headline}
        </h2>
      </div>
      <StoryMeta story={story} />
      <StoryFooter story={story} />
      {story.image && (
        <img className="story-card__image" src={story.image} alt="" loading="lazy" />
      )}
    </article>
  );
}

// Columnist: avatar and name header, headline, bold standfirst.
export function StoryCardColumnist({ story }) {
  return (
    <article className="story-card story-card--columnist">
      <header className="story-card__columnist">
        {story.authorImage ? (
          <img
            className="story-card__avatar"
            src={story.authorImage}
            alt=""
            loading="lazy"
          />
        ) : (
          <span className="story-card__avatar story-card__avatar--fallback" aria-hidden="true">
            {story.author.charAt(0)}
          </span>
        )}
        <div className="story-card__columnist-byline">
          <p className="story-card__columnist-name">{story.author}</p>
          <p className="story-card__time">
            {timeAgo(story.publishedAt)}
            {story.articleAudioUrl && <AudioBadge story={story} />}
          </p>
        </div>
        <KebabButton />
      </header>

      {/* The standfirst leads and the headline supports it, per the reference
          design. Stories without a standfirst fall back to leading with the
          headline rather than opening on an empty line. */}
      <h2 className="story-card__headline">
        {story.standfirst || story.headline}
      </h2>
      {story.standfirst && story.headline && (
        <p className="story-card__standfirst">{story.headline}</p>
      )}
    </article>
  );
}

// Full-bleed image with section label and headline overlaid.
export function StoryCardOverlay({ story }) {
  return (
    <article className="story-card story-card--overlay">
      <CardMedia story={story} />
      <div className="story-card__overlay">
        <div className="story-card__heading">
          {story.section && (
            <span className="story-card__label">{story.section}</span>
          )}
          <h2 className="story-card__overlay-headline">{story.headline}</h2>
        </div>
      </div>
      <div className="story-card__overlay-actions">
        <KebabButton light />
      </div>
    </article>
  );
}

// Audio: a compact "now playing" panel. Square episode cover on the left
// with a floating play button, episode info on the right, and a full-width
// player bar below — progress, equalizer, time and kebab. A dark surface
// distinct from the overlay card, built to play inline.
export function StoryCardAudio({ story }) {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(null);
  const [elapsed, setElapsed] = useState(0);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (audio.paused) {
      audio.play();
    } else {
      audio.pause();
    }
  };

  const started = isPlaying || elapsed > 0;
  const progress = duration ? Math.min(100, (elapsed / duration) * 100) : 0;

  return (
    <article
      className={
        isPlaying
          ? 'story-card story-card--audio is-playing'
          : 'story-card story-card--audio'
      }
    >
      <div className="story-card__audio-top">
        <div className="story-card__audio-cover">
          {story.image && (
            <img
              className="story-card__audio-art"
              src={story.image}
              alt=""
              loading="lazy"
            />
          )}
          <button
            type="button"
            className={
              isPlaying
                ? 'story-card__play story-card__play--playing'
                : 'story-card__play'
            }
            onClick={togglePlay}
            aria-label={isPlaying ? 'Pause episode' : 'Play episode'}
          >
            <span className="story-card__play-icon" aria-hidden="true">
              {isPlaying ? <PauseIcon /> : <PlayIcon />}
            </span>
          </button>
        </div>

        <div className="story-card__audio-info">
          {story.section && (
            <span className="story-card__eyebrow">{story.section}</span>
          )}
          <h2 className="story-card__headline">{story.headline}</h2>
          <p className="story-card__audio-host">
            {story.author && <span>{story.author}</span>}
            {story.author && story.publishedAt && (
              <span>{timeAgo(story.publishedAt)}</span>
            )}
          </p>
        </div>
      </div>

      {story.audioUrl && (
        <div className="story-card__audio-bar">
          <div
            className="story-card__audio-progress"
            role="progressbar"
            aria-label="Episode progress"
            aria-valuemin={0}
            aria-valuemax={Math.round(duration || 0)}
            aria-valuenow={Math.round(elapsed)}
          >
            <span
              className="story-card__audio-progress-fill"
              style={{ width: `${progress}%` }}
            />
          </div>

          <div className="story-card__audio-bar-row">
            <span
              className={
                isPlaying
                  ? 'story-card__eq story-card__eq--playing'
                  : 'story-card__eq'
              }
              aria-hidden="true"
            >
              <span />
              <span />
              <span />
              <span />
              <span />
            </span>

            <span className="story-card__audio-time">
              {started
                ? `${formatPlayTime(elapsed)}${duration ? ` / ${formatPlayTime(duration)}` : ''}`
                : duration
                  ? `${Math.max(1, Math.round(duration / 60))} min`
                  : 'Listen'}
            </span>

            <KebabButton light />

            <audio
              ref={audioRef}
              src={story.audioUrl}
              preload="metadata"
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              onEnded={() => setIsPlaying(false)}
              onLoadedMetadata={(event) => setDuration(event.target.duration)}
              onTimeUpdate={(event) => setElapsed(event.target.currentTime)}
            />
          </div>
        </div>
      )}
    </article>
  );
}

function formatPlayTime(seconds) {
  const mins = Math.floor(seconds / 60);
  const secs = String(Math.floor(seconds % 60)).padStart(2, '0');
  return `${mins}:${secs}`;
}

function StoryMeta({ story }) {
  return (
    <p className="story-card__meta">
      <span className="story-card__meta-text">
        {story.author && <span className="story-card__author">{story.author}</span>}
        {story.publishedAt && <span>{timeAgo(story.publishedAt)}</span>}
        {story.articleAudioUrl && <AudioBadge story={story} />}
      </span>
      <KebabButton />
    </p>
  );
}

function StoryFooter() {
  return null;
}

// Renders the card's media: a video placeholder when the story has a video
// stream (poster image with a central play button), otherwise the promo
// image. Tapping the placeholder plays the video inline with native controls.
// Used only by the full-width display cards (Hero, Overlay).
function CardMedia({ story }) {
  const [showVideo, setShowVideo] = useState(false);

  if (story.videoUrl) {
    if (showVideo) {
      return (
        <video
          className="story-card__image story-card__video"
          src={story.videoUrl}
          poster={story.image || undefined}
          controls
          autoPlay
          playsInline
          preload="metadata"
        />
      );
    }

    return (
      <button
        type="button"
        className="story-card__video-placeholder"
        onClick={() => setShowVideo(true)}
        aria-label={`Play video for ${story.headline}`}
      >
        {story.image && (
          <img
            className="story-card__image"
            src={story.image}
            alt=""
            loading="lazy"
          />
        )}
        <span className="story-card__video-play" aria-hidden="true">
          <PlayIcon />
        </span>
      </button>
    );
  }

  if (story.image) {
    return (
      <img
        className="story-card__image"
        src={story.image}
        alt=""
        loading="lazy"
      />
    );
  }

  return null;
}

function PlayIcon() {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" fill="currentColor">
      <path d="M5.5 3.2v9.6L13 8z" />
    </svg>
  );
}

function PauseIcon() {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" fill="currentColor">
      <rect x="4" y="3" width="3" height="10" />
      <rect x="9" y="3" width="3" height="10" />
    </svg>
  );
}

function HeadphonesIcon() {
  return (
    <svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9a5 5 0 0 1 10 0" />
      <rect x="2" y="9" width="3" height="4" rx="1" />
      <rect x="11" y="9" width="3" height="4" rx="1" />
    </svg>
  );
}

// A headphones button shown in the meta row when an article has an audio
// narration. Tapping it loads the story into the global audio player (the
// fixed bar above the bottom nav); the icon swaps to a pause glyph while
// this story is the one playing. Separated by the same dot meta uses.
function AudioBadge({ story }) {
  const { current, isPlaying, play } = useAudioPlayer();
  const isThisPlaying = current?.id === story.id && isPlaying;
  const isThisLoaded = current?.id === story.id;

  return (
    <button
      type="button"
      className={
        isThisPlaying
          ? 'story-card__audio-badge story-card__audio-badge--playing'
          : 'story-card__audio-badge'
      }
      onClick={(event) => {
        event.stopPropagation();
        play(story);
      }}
      aria-label={
        isThisPlaying
          ? `Pause audio for ${story.headline}`
          : `Play audio for ${story.headline}`
      }
    >
      {isThisLoaded ? <PauseIcon /> : <HeadphonesIcon />}
    </button>
  );
}

function KebabButton({ light = false }) {
  return (
    <button
      type="button"
      className={
        light ? 'story-card__kebab story-card__kebab--light' : 'story-card__kebab'
      }
      aria-label="More options"
    >
      <svg viewBox="0 0 16 16" width="16" height="16" fill="currentColor">
        <circle cx="3" cy="8" r="1.4" />
        <circle cx="8" cy="8" r="1.4" />
        <circle cx="13" cy="8" r="1.4" />
      </svg>
    </button>
  );
}
